import { createRoot } from "react-dom/client";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import "@/global.css";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import MainLayout from "@/components/layout/MainLayout";
import { AuthProvider } from "@/contexts/AuthContext";
import { RequireAuth } from "@/components/auth/RequireAuth";
import Login from "@/pages/Login";

import { Suspense, lazy } from "react";
import About from "@/pages/About";
import Agents from "@/pages/Agents";
import Build from "@/pages/Build";
import Compliance from "@/pages/Compliance";
import Core from "@/pages/Core";
import Design from "@/pages/Design";
import Footwear from "@/pages/design/Footwear";
import Gov from "@/pages/Gov";
import Index from "@/pages/Index";
import Media from "@/pages/Media";
import Motion from "@/pages/Motion";
import NotFound from "@/pages/NotFound";

import { STRONOM_STUDIO_LIBRARY } from "@/features/footwear-studio/featureFlags";

const FootwearStudio = lazy(() => import("@/pages/design/FootwearStudio"));
const CollectionPlanPage = lazy(
  () => import("@/features/footwear-studio/collectionPlan/CollectionPlanPage"),
);
const ArtworkPage = lazy(
  () => import("@/features/footwear-studio/artwork/ArtworkPage"),
);
const MaterialLibraryPage = lazy(
  () => import("@/pages/footwear-studio/MaterialLibrary"),
);
const StudioLibrary = lazy(() => import("@/features/footwear-studio/StudioLibrary"));
const GalleryView = lazy(() => import("@/pages/gallery/[runId]"));

const JobsIndex = lazy(() => import("@/pages/jobs/JobsIndex"));
const JobDetail = lazy(() => import("@/pages/jobs/JobDetail"));
const FootwearRulesDebug = lazy(() => import("@/pages/debug/footwear-rules"));
const SandboxIntentTest = lazy(() => import("@/pages/sandbox-intent-test"));
const ReviewTrackProbe = lazy(() => import("@/pages/dev/ReviewTrackProbe"));
const ReviewOverlayProbe = lazy(() => import("@/pages/dev/ReviewOverlayProbe"));
const StyleDetailLoadingProbe = lazy(
  () => import("@/pages/dev/StyleDetailLoadingProbe"),
);

const queryClient = new QueryClient();

const PageLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="text-center space-y-3">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            {/* Gallery view - standalone page without layout */}
            <Route
              path="gallery/:runId"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <GalleryView />
                </Suspense>
              }
            />

            <Route path="login" element={<Login />} />

            {import.meta.env.DEV ? (
              <Route
                path="dev/review-track-probe"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ReviewTrackProbe />
                  </Suspense>
                }
              />
            ) : null}

            {import.meta.env.DEV ? (
              <Route
                path="dev/review-overlay-probe"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <ReviewOverlayProbe />
                  </Suspense>
                }
              />
            ) : null}

            {import.meta.env.DEV ? (
              <Route
                path="dev/style-detail-loading-probe"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <StyleDetailLoadingProbe />
                  </Suspense>
                }
              />
            ) : null}

            {/* Main layout routes */}
            <Route element={<RequireAuth><MainLayout /></RequireAuth>}>
            <Route index element={<Index />} />
            <Route path="about" element={<About />} />
            <Route path="core" element={<Core />} />
            <Route path="design" element={<Design />} />
            <Route path="design/footwear" element={<Footwear />} />
            {/* Footwear Studio — canonical entry */}
            <Route
              path="footwear-studio"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <FootwearStudio />
                </Suspense>
              }
            />
            <Route
              path="footwear-studio/collection-plan"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <CollectionPlanPage />
                </Suspense>
              }
            />
            <Route
              path="footwear-studio/artwork"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <ArtworkPage />
                </Suspense>
              }
            />
            <Route
              path="footwear-studio/artwork/:styleId"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <ArtworkPage />
                </Suspense>
              }
            />
            <Route
              path="footwear-studio/materials"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <MaterialLibraryPage />
                </Suspense>
              }
            />
            <Route
              path="footwear-studio/moodboard"
              element={<Navigate to="/footwear-studio/collection-plan" replace />}
            />
            {STRONOM_STUDIO_LIBRARY ? (
              <Route
                path="footwear-studio/library"
                element={
                  <Suspense fallback={<PageLoadingFallback />}>
                    <StudioLibrary />
                  </Suspense>
                }
              />
            ) : null}
            {/* Legacy paths for backward compatibility */}
            <Route
              path="design/footwear-studio"
              element={<Navigate to="/footwear-studio" replace />}
            />
            <Route
              path="design/footwear-studio/moodboard"
              element={<Navigate to="/footwear-studio/collection-plan" replace />}
            />
            <Route
              path="design/moodboard"
              element={<Navigate to="/footwear-studio/collection-plan" replace />}
            />
            <Route path="build" element={<Build />} />
            <Route path="motion" element={<Motion />} />
            <Route path="compliance" element={<Compliance />} />
            <Route path="media" element={<Media />} />
            <Route path="gov" element={<Gov />} />
            <Route path="agents" element={<Agents />} />
            <Route
              path="jobs"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <JobsIndex />
                </Suspense>
              }
            />
            <Route
              path="jobs/:id"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <JobDetail />
                </Suspense>
              }
            />
            <Route
              path="debug/footwear-rules"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <FootwearRulesDebug />
                </Suspense>
              }
            />
            <Route
              path="debug/sandbox-intent-test"
              element={
                <Suspense fallback={<PageLoadingFallback />}>
                  <SandboxIntentTest />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </BrowserRouter>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

createRoot(document.getElementById("root")!).render(<App />);
